import React, { useState, useEffect } from "react";
import { CheckSquare, Square, Trash2, Plus, Edit3, X, Save, Package } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";

export const ChecklistCard = ({ checklist, onUpdate, onDelete, onConvertedToInventory }) => {
    const [editing, setEditing] = useState(false);
    const [title, setTitle] = useState(checklist.title);
    const [items, setItems] = useState(checklist.items || []);
    const [newItem, setNewItem] = useState("");
    const [converting, setConverting] = useState(false);

    useEffect(() => {
        setTitle(checklist.title);
        setItems(checklist.items || []);
    }, [checklist]);

    const allComplete = items.length > 0 && items.every((i) => i.completed);

    // Auto-reset when all complete
    useEffect(() => {
        if (!editing && allComplete) {
            const t = setTimeout(() => {
                const reset = items.map((i) => ({ ...i, completed: false }));
                onUpdate(checklist.id, { items: reset });
            }, 1500);
            return () => clearTimeout(t);
        }
    }, [allComplete, editing]);

    const toggle = (id) => {
        const next = items.map((i) => (i.id === id ? { ...i, completed: !i.completed } : i));
        setItems(next);
        onUpdate(checklist.id, { items: next });
    };

    const save = async () => {
        await onUpdate(checklist.id, { title, items });
        setEditing(false);
    };

    const addItem = () => {
        const t = newItem.trim();
        if (!t) return;
        const next = [...items, { id: Math.random().toString(36).slice(2), text: t, completed: false }];
        setItems(next);
        setNewItem("");
    };

    return (
        <div
            className="bg-meye-card border border-meye-border rounded-md p-4 fade-up relative overflow-hidden"
            data-testid={`checklist-card-${checklist.id}`}
        >
            {allComplete && !editing && (
                <div className="absolute inset-0 bg-meye-cyan/5 pointer-events-none">
                    <div className="scan-line" />
                </div>
            )}
            <div className="relative">
                <div className="flex items-center justify-between mb-3">
                    {editing ? (
                        <input
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="flex-1 bg-black/40 border border-meye-border rounded px-2 py-1 text-sm font-brand text-zinc-100 focus:outline-none focus:border-meye-cyan/60"
                            data-testid={`checklist-title-edit-${checklist.id}`}
                        />
                    ) : (
                        <h3 className="font-brand text-base text-zinc-100 uppercase tracking-wider">{checklist.title}</h3>
                    )}
                    <div className="flex gap-2 ml-2">
                        {editing ? (
                            <>
                                <button
                                    onClick={() => {
                                        setEditing(false);
                                        setTitle(checklist.title);
                                        setItems(checklist.items || []);
                                    }}
                                    className="text-zinc-500 hover:text-white"
                                >
                                    <X size={14} />
                                </button>
                                <button
                                    onClick={save}
                                    className="text-meye-cyan hover:text-cyan-300"
                                    data-testid={`checklist-save-${checklist.id}`}
                                >
                                    <Save size={14} />
                                </button>
                            </>
                        ) : (
                            <>
                                {/* One-tap fix for the common "I dictated my bins and it all landed
                                    in Checklists" mishap. Parses "1. drill, screwdriver" style items
                                    into inventory rows with bin numbers and removes the source
                                    checklist. Confirmation required — this is destructive.  */}
                                <button
                                    onClick={async () => {
                                        if (converting) return;
                                        const confirmed = window.confirm(
                                            `Move all ${items.length} items in "${checklist.title}" to Inventory?\n\nEach item becomes an inventory row (bin numbers parsed from "1.", "Bin 1:", etc.). The checklist is removed.`,
                                        );
                                        if (!confirmed) return;
                                        setConverting(true);
                                        try {
                                            const res = await api.convertChecklistToInventory(checklist.id);
                                            toast.success(`Moved ${res.inventory_created} items to Inventory`);
                                            onConvertedToInventory?.(checklist.id, res.items);
                                        } catch (e) {
                                            console.error(e);
                                            toast.error("Convert failed");
                                        } finally {
                                            setConverting(false);
                                        }
                                    }}
                                    disabled={converting || !items.length}
                                    className="text-zinc-500 hover:text-meye-fuchsia disabled:opacity-40"
                                    data-testid={`checklist-convert-to-inventory-${checklist.id}`}
                                    title="Move all items into Inventory"
                                    aria-label="Convert to Inventory"
                                >
                                    <Package size={14} />
                                </button>
                                <button
                                    onClick={() => setEditing(true)}
                                    className="text-zinc-500 hover:text-meye-cyan"
                                    data-testid={`checklist-edit-${checklist.id}`}
                                >
                                    <Edit3 size={14} />
                                </button>
                                <button
                                    onClick={() => onDelete(checklist.id)}
                                    className="text-zinc-500 hover:text-red-400"
                                    data-testid={`checklist-delete-${checklist.id}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <div className="space-y-2">
                    {items.map((item, idx) =>
                        editing ? (
                            <div key={item.id} className="flex items-center gap-2">
                                <input
                                    value={item.text}
                                    onChange={(e) => {
                                        const next = [...items];
                                        next[idx] = { ...item, text: e.target.value };
                                        setItems(next);
                                    }}
                                    className="flex-1 bg-transparent border-b border-meye-border text-sm text-zinc-200 focus:outline-none focus:border-meye-cyan/60 py-1"
                                />
                                <button
                                    onClick={() => setItems(items.filter((_, i) => i !== idx))}
                                    className="text-zinc-600 hover:text-red-400"
                                >
                                    <X size={12} />
                                </button>
                            </div>
                        ) : (
                            <button
                                key={item.id}
                                onClick={() => toggle(item.id)}
                                className="flex items-start gap-2.5 w-full text-left group"
                                data-testid={`checklist-item-${item.id}`}
                            >
                                {item.completed ? (
                                    <CheckSquare size={16} className="text-meye-cyan mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                                ) : (
                                    <Square size={16} className="text-zinc-600 group-hover:text-zinc-400 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                                )}
                                <span
                                    className={`text-sm ${item.completed ? "line-through text-zinc-500" : "text-zinc-200"}`}
                                >
                                    {item.text}
                                </span>
                            </button>
                        ),
                    )}
                </div>

                {editing && (
                    <div className="flex items-center gap-2 mt-3">
                        <input
                            value={newItem}
                            onChange={(e) => setNewItem(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && addItem()}
                            placeholder="add item..."
                            className="flex-1 bg-black/40 border border-meye-border rounded px-2 py-1 text-sm text-zinc-100 focus:outline-none focus:border-meye-cyan/60"
                            data-testid={`checklist-new-item-${checklist.id}`}
                        />
                        <button
                            onClick={addItem}
                            className="text-meye-cyan hover:text-cyan-300"
                            data-testid={`checklist-add-item-${checklist.id}`}
                        >
                            <Plus size={16} />
                        </button>
                    </div>
                )}

                {allComplete && !editing && (
                    <p className="text-[10px] uppercase tracking-widest text-meye-cyan mt-3 font-mono">
                        · resetting routine ·
                    </p>
                )}
            </div>
        </div>
    );
};

export default ChecklistCard;
